import type { ApiErrorBody, MediaDto } from '@studio/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function toError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* not JSON */
  }
  const e = body.error;
  return new ApiError(
    res.status,
    e?.code ?? 'HTTP_ERROR',
    e?.message ?? `Request failed (${res.status})`,
    e?.details as ApiError['details'],
  );
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/** JSON API call with the session cookie. */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401 && !path.startsWith('/api/auth/login')) onUnauthorized?.();
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Uploads one picture; returns the stored file. */
export async function uploadImage(file: File): Promise<MediaDto> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/media', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body: form,
  });
  if (!res.ok) throw await toError(res);
  return (await res.json()) as MediaDto;
}

export const errorMessage = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');
